import React, { useCallback, useMemo, useState } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";

export function parseSemicolonList(str) {
  if (!str) return [];
  return String(str)
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function joinSemicolonList(items) {
  const unique = [];
  const seen = new Set();
  for (const raw of items || []) {
    const item = String(raw || "").trim();
    if (!item) continue;
    const key = item.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
  }
  return unique.join("; ");
}

/**
 * Lista de valores livres persistida como string separada por `;`.
 * UX: chips — Enter / ; / blur / paste com vários itens.
 */
export default function SemicolonListInput({
  label,
  value,
  onChange,
  helperText,
  placeholder = "Digite e pressione Enter",
  disabled = false,
}) {
  const [input, setInput] = useState("");
  const chips = useMemo(() => parseSemicolonList(value), [value]);

  const commitItems = useCallback(
    (rawItems) => {
      const next = joinSemicolonList([...chips, ...(rawItems || [])]);
      if (next !== joinSemicolonList(chips)) {
        onChange(next);
      }
      setInput("");
    },
    [chips, onChange]
  );

  const commitFreeText = useCallback(
    (raw) => {
      const parts = parseSemicolonList(String(raw || "").replace(/,/g, ";"));
      if (!parts.length) return;
      commitItems(parts);
    },
    [commitItems]
  );

  return (
    <Autocomplete
      multiple
      freeSolo
      fullWidth
      options={[]}
      open={false}
      forcePopupIcon={false}
      disableClearable
      value={chips}
      inputValue={input}
      disabled={disabled}
      onInputChange={(_, next, reason) => {
        if (reason === "reset") return;
        // Digitar `;` finaliza o item atual (como no formato legado).
        if (next.includes(";")) {
          const [head, ...rest] = next.split(";");
          const pending = rest.join(";");
          if (String(head || "").trim()) {
            commitFreeText(head);
          } else {
            setInput("");
          }
          // Mantém eventual texto após o `;` (ex.: colar parcial).
          if (pending) setInput(pending);
          return;
        }
        setInput(next);
      }}
      onChange={(_, newValue) => {
        onChange(joinSemicolonList(newValue || []));
        setInput("");
      }}
      onBlur={() => {
        if (input.trim()) commitFreeText(input);
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((chip, index) => (
          <Chip {...getTagProps({ index })} key={`${chip}-${index}`} label={chip} size="small" />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={chips.length ? "" : placeholder}
          helperText={helperText}
          onPaste={(e) => {
            const text = e.clipboardData?.getData("text");
            if (!text || (!text.includes(";") && !text.includes("\n"))) return;
            e.preventDefault();
            const parts = String(text)
              .split(/[;\n]+/)
              .map((s) => s.trim())
              .filter(Boolean);
            if (parts.length) commitItems(parts);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Evita submit do form pai; MUI não adiciona o chip sozinho com open={false}.
              e.preventDefault();
              e.stopPropagation();
              if (input.trim()) commitFreeText(input);
            } else if (e.key === "Backspace" && !input && chips.length) {
              onChange(joinSemicolonList(chips.slice(0, -1)));
            }
          }}
        />
      )}
    />
  );
}
